<template>
  <div class="posters-list-container">
    <h1>保養知識</h1>

    <!-- 搜尋列 -->
    <div class="search-bar">
      <input
        v-model="searchInput"
        type="text"
        placeholder="搜尋文章標題..."
        class="search-input"
        @keyup.enter="doSearch"
      />
      <button class="search-btn" @click="doSearch">搜尋</button>
      <button v-if="search" class="clear-btn" @click="clearSearch">✕ 清除</button>
    </div>

    <div v-if="loading" class="loading">載入中...</div>
    <div v-else-if="errorMsg" class="error">{{ errorMsg }}</div>

    <template v-else>
      <!-- 搜尋結果提示 -->
      <p v-if="search" class="search-hint">
        「{{ search }}」搜尋結果：共 {{ total }} 篇
      </p>
      <p v-if="search && total === 0" class="no-result">找不到相關文章，請換個關鍵字試試</p>

      <div class="posters-grid">
        <router-link
          v-for="poster in posters"
          :key="poster.id"
          :to="{ path: `/posters/${poster.id}`, query: { fromPage: page } }"
          class="poster-card"
        >
          <div class="card-image">
            <img
              v-if="poster.coverImage"
              :src="poster.coverImage"
              :alt="poster.title"
            />
            <div v-else class="no-image">暫無圖片</div>
          </div>
          <div class="card-body">
            <h3 class="card-title">{{ poster.title }}</h3>
            <p class="card-desc">{{ poster.seo_description || '' }}</p>
          </div>
        </router-link>
      </div>

      <!-- 分頁 -->
      <div v-if="totalPages > 1" class="pagination">
        <button
          class="page-btn"
          :disabled="page <= 1"
          @click="goToPage(page - 1)"
        >
          &laquo; 上一頁
        </button>

        <button
          v-for="p in visiblePages"
          :key="p"
          class="page-btn"
          :class="{ active: p === page }"
          @click="goToPage(p)"
        >
          {{ p }}
        </button>

        <button
          class="page-btn"
          :disabled="page >= totalPages"
          @click="goToPage(page + 1)"
        >
          下一頁 &raquo;
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

const posters = ref([])
const loading = ref(true)
const errorMsg = ref('')
const page = ref(1)
const totalPages = ref(1)
const total = ref(0)
const limit = 9

// 搜尋
const searchInput = ref('')  // 輸入框即時值
const search = ref('')       // 實際送出的關鍵字

async function fetchPosters() {
  loading.value = true
  errorMsg.value = ''
  try {
    const params = { page: page.value, limit }
    if (search.value) params.search = search.value
    const res = await axios.get('/api/load/posters', { params })
    posters.value = res.data.rows
    totalPages.value = res.data.totalPages
    total.value = res.data.total
  } catch (err) {
    console.error(err)
    errorMsg.value = '載入文章列表失敗'
  } finally {
    loading.value = false
  }
}

function doSearch() {
  search.value = searchInput.value.trim()
  page.value = 1
  router.replace({ query: search.value ? { search: search.value } : {} })
  fetchPosters()
}

function clearSearch() {
  searchInput.value = ''
  search.value = ''
  page.value = 1
  router.replace({ query: {} })
  fetchPosters()
}

function goToPage(p) {
  if (p < 1 || p > totalPages.value || p === page.value) return
  page.value = p
  const q = search.value ? { search: search.value, page: p } : { page: p }
  router.replace({ query: q })
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const visiblePages = computed(() => {
  const tot = totalPages.value
  const current = page.value
  const pages = []
  let start = Math.max(1, current - 2)
  let end = Math.min(tot, current + 2)
  if (end - start < 4) {
    if (start === 1) end = Math.min(tot, start + 4)
    else start = Math.max(1, end - 4)
  }
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
})

onMounted(() => {
  const queryPage = parseInt(route.query.page)
  if (queryPage > 0) page.value = queryPage
  if (route.query.search) {
    search.value = route.query.search
    searchInput.value = route.query.search
  }
  fetchPosters()
})

watch(page, () => {
  fetchPosters()
})
</script>

<style scoped>
.posters-list-container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px 20px 30px;
}

.posters-list-container h1 {
  text-align: center;
  margin-bottom: 16px;
  font-size: 1.6rem;
  color: #333;
}

/* 搜尋列 */
.search-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.search-input {
  width: 280px;
  max-width: 100%;
  padding: 8px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;
}
.search-input:focus {
  border-color: #ff6600;
}
.search-btn {
  padding: 8px 18px;
  background: #ff6600;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;
}
.search-btn:hover {
  background: #e55a00;
}
.clear-btn {
  padding: 8px 14px;
  background: #f0f0f0;
  color: #666;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s;
}
.clear-btn:hover {
  background: #e0e0e0;
}
.search-hint {
  text-align: center;
  font-size: 0.88rem;
  color: #888;
  margin-bottom: 12px;
}
.no-result {
  text-align: center;
  padding: 40px 20px;
  color: #aaa;
  font-size: 1rem;
}

.loading,
.error {
  text-align: center;
  padding: 40px;
  font-size: 1.1rem;
}
.error {
  color: red;
}

/* Grid */
.posters-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

/* Card */
.poster-card {
  text-decoration: none;
  color: inherit;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  transition: transform 0.25s, box-shadow 0.25s;
  display: flex;
  flex-direction: column;
}
.poster-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
}

.card-image {
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #f0f0f0;
}
.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* 封面主標在圖片上方，縮圖固定顯示上段 */
  object-position: center top;
  transform-origin: center top;
  transition: transform 0.3s;
}
.poster-card:hover .card-image img {
  transform: scale(1.05);
}
.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
  font-size: 0.9rem;
}

.card-body {
  padding: 10px 12px;
}
.card-title {
  font-size: 0.92rem;
  font-weight: 600;
  margin: 0 0 4px;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}
.card-desc {
  font-size: 0.8rem;
  color: #888;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
  flex-wrap: wrap;
}
.page-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #555;
  transition: all 0.2s;
}
.page-btn:hover:not(:disabled):not(.active) {
  background: #f5f5f5;
  border-color: #bbb;
}
.page-btn.active {
  background: #333;
  color: #fff;
  border-color: #333;
}
.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* RWD */
@media (max-width: 900px) {
  .posters-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }
}
@media (max-width: 550px) {
  .posters-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .posters-list-container {
    padding: 16px 12px 24px;
  }
}
</style>
